import { CloseOutlined } from "@ant-design/icons";
import { Tree } from "antd";
import { DirectoryTreeProps } from "antd/es/tree";
import { FC, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  addRecentlySearchedQuery,
} from '../api/searchHistoryApi';
import { buildSearchRouteQuery } from '../domain/queryCodec';
import { SearchHistoryNode, useSearchStore } from '../store/useSearchStore';

import { BasicResponseDTO } from "@dto/basicResponse.dto";
import { AddRecentlySearchedDTO } from "@dto/recentlySearched.dto";
import { Log } from 'src/service/log.service';
import { postApi } from "src/util/apiCalls";
import '../styles/SearchHistory.less';

interface Props {
  historyState: number;
  userId: number;
}

const deleteRecentlySearchedQuery = async (
  userId: number,
  searchQuery: string
): Promise<void> => {
  const payload: AddRecentlySearchedDTO = {
    user_id: String(userId),
    search_query: searchQuery,
  };

  await postApi<BasicResponseDTO, AddRecentlySearchedDTO>(
    '/api/search/v1/recently_searched/delete',
    payload
  );
};

const HistorySearch: FC<Props> = ({ historyState, userId }) => {
  const navigate = useNavigate();
  const { search } = useLocation();

  const queryStr: string = search.startsWith('?') ? search.slice(1) : search;
  const selectedKey: string | undefined = queryStr || undefined;

  const setSearch = useSearchStore(s => s.setSearch);
  const historyData = useSearchStore(s => s.historyData);
  const reloadHistoryData = useSearchStore(s => s.reloadHistoryData);
  const deleteRequestSeqRef = useRef(0);
  const pendingDeleteIdsRef = useRef<Set<string>>(new Set());

  // Ensure the currently active item is always rendered first
  const sortedHistoryData = useMemo(() => {
    const seen = new Set<string>();
    const deduped = historyData.filter((item) => {
      const key = String(item.key);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (!selectedKey) return deduped;
    const idx = deduped.findIndex((item) => String(item.key) === selectedKey);
    if (idx <= 0) return deduped;
    return [deduped[idx], ...deduped.slice(0, idx), ...deduped.slice(idx + 1)];
  }, [historyData, selectedKey]);

  useEffect(() => {
    if (userId && historyState && historyData.length === 0) {
      void reloadHistoryData();
    }
  }, [historyState, userId, reloadHistoryData, historyData.length]);

  const persistHistorySelection = async (inputSearch: string) => {
    try {
      await addRecentlySearchedQuery(userId, inputSearch);
      await reloadHistoryData();
    } catch (error) {
      Log.error('ERROR in handleHistory', error);
    }
  };

  const handleHistory: DirectoryTreeProps<SearchHistoryNode>['onClick'] = (event, node) => {
    const menuNode = node as unknown as SearchHistoryNode;
    const inputSearch = menuNode.titleText;

    const target = event.target as HTMLElement;

    // Ignore clicks on the delete icon so we don't accidentally re-add what we just deleted
    if (target.closest('.anticon-close')) {
      return;
    }

    if (
      target.classList.contains("ant-tree-title") ||
      target.classList.contains("ant-tree-node-content-wrapper") ||
      target.closest('.ant-tree-title') ||
      target.closest('.ant-tree-node-content-wrapper')
    ) {
      navigate(`/search?${String(menuNode.key)}`);
      setSearch(inputSearch);
      void persistHistorySelection(inputSearch);
    }
  };

  const onDeleteHistory = async (id: string) => {
    if (pendingDeleteIdsRef.current.has(id)) {
      return;
    }

    const requestSeq = ++deleteRequestSeqRef.current;
    pendingDeleteIdsRef.current.add(id);

    try {
      await deleteRecentlySearchedQuery(userId, id);
    } catch (error) {
      Log.error('Failed to delete search history', error);
      pendingDeleteIdsRef.current.delete(id);
      return;
    }

    try {
      pendingDeleteIdsRef.current.delete(id);
      await reloadHistoryData();

      // Ignore stale completions when multiple deletions are triggered quickly.
      if (requestSeq !== deleteRequestSeqRef.current) {
        return;
      }

      const updatedHistoryData = useSearchStore.getState().historyData;

      if (updatedHistoryData.length === 0) {
        setSearch('');
        navigate('/search');
        return;
      }

      const nextItem = updatedHistoryData[0];
      if (!nextItem?.titleText) {
        setSearch('');
        navigate('/search');
        return;
      }

      navigate(`/search?${buildSearchRouteQuery(nextItem.titleText)}`);
      setSearch(nextItem.titleText);
    } catch (error) {
      Log.error('Failed to reload search history after deletion', error);
    }
  };

  return (
    <>
      {historyData.length > 0 && (
        <>
          <h3>History</h3>
          <Tree.DirectoryTree
            className='left-tree-history-delete'
            selectedKeys={selectedKey ? [selectedKey] : []}
            defaultExpandParent={true}
            showIcon={true}
            showLine={false}
            onClick={handleHistory}
            treeData={sortedHistoryData.map((obj) => ({
              ...obj,
              icon: (
                <CloseOutlined
                  style={{ fontSize: 8 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    void onDeleteHistory(obj.titleText);
                  }}
                />
              ),
            }))}
          />
        </>
      )}
    </>
  );
};

export default HistorySearch;
